import React from "react";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Link,
  Slide,
  styled,
  Typography,
  useTheme,
} from "@mui/material";
import { BankConnectionMetaImportProgressEnum } from "@bn/api/apiv3";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import SuccessIcon from "@icons/myicons/check-circle-filled.svg?react";
import ErrorIcon from "@icons/myicons/error-filled.svg?react";
import ArrowDownIcon from "@icons/myicons/arrow-down.svg?react";
import { CloseButton, LoadingSpinner } from "@components";
import { useBankImportContext } from "@providers/BankImportStatusProvider/BankImportStatusProvider.tsx";
import AccountPreviewIcon from "@components/Accounts/AccountPreviewCard/AccountPreviewIcon.tsx";
import { getRedirectType } from "@components/Accounts/utils.ts";

const StyledArrowDownIcon = styled(Box)<{ isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  transform: rotate(${({ isCollapsed }) => (isCollapsed ? "0" : "180")}deg);
  transition: transform 0.2s ease-in-out;
  transform-origin: center;
`;

const Wrapper = styled(Box)(({ theme }) => ({
  zIndex: 10000,
  position: "fixed",
  width: "366px",
  bottom: theme.spacing(6),
  right: theme.spacing(6),
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1.5, 2),
  border: `2px solid ${theme.palette.border.secondary}`,
  boxShadow: theme.shadows[1],
  borderRadius: `${theme.shape.borderRadius}px`,
}));

const Icon = () => {
  const { status } = useBankImportContext();
  const theme = useTheme();

  switch (status) {
    case BankConnectionMetaImportProgressEnum.SUCCESS:
      return (
        <SuccessIcon
          color={theme.palette.icon.success}
          width={28}
          height={28}
        />
      );
    case BankConnectionMetaImportProgressEnum.FAILED_WITH_ERROR:
      return (
        <ErrorIcon color={theme.palette.icon.error} width={28} height={28} />
      );
    default:
      return <LoadingSpinner width={24} />;
  }
};

const ErrorMessage = () => {
  const { closeMessage, type } = useBankImportContext();
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      gap={3}
      data-testid={`bank-status-message-error-${type}`}
    >
      <Box display="flex" gap={1.5} alignItems="center">
        <Icon />
        <Typography variant="body3" color="textSecondary">
          {t(
            `bankImportStatus.error${type === "import" ? "Import" : "Update"}`
          )}
        </Typography>
      </Box>
      <CloseButton onClose={closeMessage} size="small" />
    </Box>
  );
};

const SuccessMessage = () => {
  const { closeMessage, accounts, type } = useBankImportContext();
  const [areMessagesOpen, setAreMessagesOpen] = React.useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box data-testid={`bank-status-message-success-${type}`}>
      <Box display="flex" justifyContent="space-between" gap={3}>
        <Box display="flex" gap={1.5} alignItems="center">
          <Icon />
          <Typography variant="body3" color="primary.main">
            {t(
              `bankImportStatus.success${
                type === "update" ? "Update" : "Import"
              }`
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {Boolean(accounts?.length) && (
            <IconButton
              size="small"
              onClick={() => {
                setAreMessagesOpen(!areMessagesOpen);
              }}
            >
              <StyledArrowDownIcon isCollapsed={!areMessagesOpen}>
                <ArrowDownIcon />
              </StyledArrowDownIcon>
            </IconButton>
          )}
          <CloseButton onClose={closeMessage} size="small" />
        </Box>
      </Box>
      <Collapse in={areMessagesOpen}>
        <Box maxHeight="300px" overflow="auto" pt={3} px={1}>
          <Typography
            variant="caption"
            color="textSecondary"
            textTransform="uppercase"
          >
            {t(
              `bankImportStatus.${
                type === "import" ? "importedData" : "updatedData"
              }`
            )}
          </Typography>
          {accounts?.map((account, index) => {
            const redirectType = getRedirectType(account);
            return (
              <Box
                pt={3}
                pb={index === accounts.length - 1 ? 1 : 3}
                key={account.id}
              >
                <Box
                  key={account.id}
                  display="flex"
                  gap={2.5}
                  alignItems="center"
                >
                  <AccountPreviewIcon
                    isReference={Boolean(account.depotIds?.length)}
                    iconUrl={account.iconUrl}
                    type={account.type}
                    size={24}
                  />
                  <Link
                    onClick={() => navigate(`/${redirectType}/${account.id}`)}
                    variant="caption"
                    color="primary.main"
                  >
                    {account.name}
                  </Link>
                </Box>
                {account.referenceAccount && (
                  <Box
                    key={account.id}
                    display="flex"
                    gap={1.5}
                    pt={1}
                    pl={1}
                    alignItems="center"
                  >
                    <AccountPreviewIcon
                      isReference
                      iconUrl={account.referenceAccount.iconUrl}
                      type={account.referenceAccount.type}
                      size={24}
                    />
                    <Link
                      onClick={() =>
                        navigate(`/accounts/${account.referenceAccount?.id}`)
                      }
                      variant="caption"
                      color="primary.main"
                    >
                      {account.referenceAccount.name}
                    </Link>
                  </Box>
                )}
                {index < accounts.length - 1 && <Divider />}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

const InProgress = ({ progress }: { progress: number }) => {
  const { status } = useBankImportContext();
  const { t } = useTranslation();
  return (
    <Box display="flex" justifyContent="space-between" gap={3} px={2} py={1.75}>
      <Box display="flex" gap={2.5} alignItems="center">
        <Icon />
        <Typography variant="body3" color="textSecondary">
          {progress}%
        </Typography>
        <Typography variant="body3">
          {t(`bankImportStatus.status.${status}`)}
        </Typography>
      </Box>
    </Box>
  );
};

export const BankStatusMessage = () => {
  const { status } = useBankImportContext();

  switch (status) {
    case BankConnectionMetaImportProgressEnum.CONNECTING:
      return <InProgress progress={10} />;
    case BankConnectionMetaImportProgressEnum.ACCOUNTS:
      return <InProgress progress={20} />;
    case BankConnectionMetaImportProgressEnum.TRANSACTIONS:
      return <InProgress progress={40} />;
    case BankConnectionMetaImportProgressEnum.PERSISTING_TRANSACTIONS:
      return <InProgress progress={50} />;
    case BankConnectionMetaImportProgressEnum.PERSISTING_BOOKINGS:
      return <InProgress progress={60} />;
    case BankConnectionMetaImportProgressEnum.SECURITIES:
      return <InProgress progress={80} />;
    case BankConnectionMetaImportProgressEnum.SUCCESS:
      return <SuccessMessage />;
    case BankConnectionMetaImportProgressEnum.FAILED_WITH_ERROR:
      return <ErrorMessage />;
    default:
      return null;
  }
};

export const BankStatus = () => {
  const { status } = useBankImportContext();

  return (
    <Slide in={!!status} direction="left">
      <Wrapper data-testId="bank-status-message">
        <BankStatusMessage />
      </Wrapper>
    </Slide>
  );
};
